/**
 * React Query hooks for MCP (AI Chat) service
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import * as mcpService from '@/api/services/mcp.service';
import type {
  MCPChatResponse,
  MCPConfirmActionResponse,
  ToolsListResponse,
} from '@/api/types/mcp.types';

// ============================================================================
// Query Keys
// ============================================================================

export const mcpKeys = {
  all: ['mcp'] as const,
  tools: () => [...mcpKeys.all, 'tools'] as const,
  health: () => [...mcpKeys.all, 'health'] as const,
};

// ============================================================================
// Chat Hook
// ============================================================================

export const useMCPChat = () => {
  return useMutation<
    MCPChatResponse,
    Error,
    { message: string; confirmed?: boolean }
  >({
    mutationFn: async ({ message, confirmed = false }) => {
      return mcpService.chat(message, confirmed);
    },
  });
};

// ============================================================================
// Confirm Action Hook
// ============================================================================

export const useMCPConfirmAction = () => {
  return useMutation<
    MCPConfirmActionResponse,
    Error,
    { toolName: string; toolArgs: Record<string, any>; confirmed: boolean }
  >({
    mutationFn: async ({ toolName, toolArgs, confirmed }) => {
      return mcpService.confirmAction(toolName, toolArgs, confirmed);
    },
  });
};

// ============================================================================
// Tools List Hook
// ============================================================================

export const useMCPTools = () => {
  return useQuery<ToolsListResponse, Error>({
    queryKey: mcpKeys.tools(),
    queryFn: mcpService.getTools,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour (replaces cacheTime)
  });
};

// ============================================================================
// Health Check Hook
// ============================================================================

export const useMCPHealth = () => {
  return useQuery({
    queryKey: mcpKeys.health(),
    queryFn: mcpService.healthCheck,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 1,
  });
};
